import { useCallback, useEffect, useMemo, useState } from "react";
import { Activity, BarChart3, Download, Eye, FileDown, Lightbulb, MousePointerClick, Smartphone, TrendingUp, Users } from "lucide-react";
import { Area, AreaChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { api, formatErr } from "../../lib/api";

const COLORS = ["#111827", "#2563EB", "#F97316", "#059669", "#7C3AED", "#DC2626", "#64748B"];

const defaultFilters = {
  range: "last_30_days",
  start: "",
  end: "",
  deviceType: "",
  source: "",
  pageType: "",
};

function fmt(value) {
  if (value === undefined || value === null || value === "") return "-";
  if (typeof value === "number") return value.toLocaleString("en-IN");
  return String(value);
}

function shortDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function qs(filters) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  return params.toString();
}

function Card({ label, value, Icon }) {
  return (
    <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6B7280]">{label}</div>
          <div className="mt-2 break-words text-2xl font-extrabold text-[#111827]">{fmt(value)}</div>
        </div>
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-[#111827] text-white">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

function Panel({ title, children, action }) {
  return (
    <section className="rounded-sm border border-[#E5E7EB] bg-white">
      <div className="flex flex-col gap-2 border-b border-[#E5E7EB] p-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="font-display text-lg font-extrabold text-[#111827]">{title}</h2>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function FilterSelect({ value, onChange, children }) {
  return (
    <select className="h-10 rounded-md border border-[#E5E7EB] bg-white px-3 text-sm" value={value} onChange={(e) => onChange(e.target.value)}>
      {children}
    </select>
  );
}

export default function AdminWebsiteAnalytics() {
  const [filters, setFilters] = useState(defaultFilters);
  const [summary, setSummary] = useState({ cards: {}, conversion: {}, charts: {} });
  const [pages, setPages] = useState({ items: [], charts: {} });
  const [downloads, setDownloads] = useState({ items: [], charts: {} });
  const [events, setEvents] = useState({ items: [], eventCounts: [] });
  const [tips, setTips] = useState([]);
  const [settings, setSettings] = useState({ enabled: true });
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("");

  const query = useMemo(() => qs(filters), [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setNotice("");
    try {
      const [settingsRes, summaryRes, pagesRes, downloadsRes, eventsRes, tipsRes] = await Promise.all([
        api.get("/analytics/settings"),
        api.get(`/analytics/summary?${query}`),
        api.get(`/analytics/pages?${query}`),
        api.get(`/analytics/downloads?${query}`),
        api.get(`/analytics/events?${query}`),
        api.get(`/analytics/tips?${query}`),
      ]);
      setSettings(settingsRes.data || { enabled: true });
      setSummary(summaryRes.data || { cards: {}, conversion: {}, charts: {} });
      setPages(pagesRes.data || { items: [], charts: {} });
      setDownloads(downloadsRes.data || { items: [], charts: {} });
      setEvents(eventsRes.data || { items: [], eventCounts: [] });
      setTips(tipsRes.data?.items || []);
    } catch (err) {
      setNotice(formatErr(err));
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  async function toggleAnalytics() {
    try {
      const { data } = await api.put("/analytics/settings", { enabled: !settings.enabled });
      setSettings(data);
      setNotice(data.enabled ? "Website analytics enabled." : "Website analytics disabled.");
    } catch (err) {
      setNotice(formatErr(err));
    }
  }

  const cards = summary.cards || {};
  const conversion = summary.conversion || {};
  const charts = summary.charts || {};

  return (
    <div className="space-y-5">
      <PageHeader
        overline="Admin / Website"
        title="Website Analytics"
        subtitle="Track page visits, downloads, lead actions, visitor behavior, and improvement tips."
      >
        <Button variant="outline" className="rounded-sm" onClick={toggleAnalytics}>
          {settings.enabled ? "Disable Analytics" : "Enable Analytics"}
        </Button>
      </PageHeader>

      {notice && <div className="rounded-sm border border-[#E5E7EB] bg-[#F9FAFB] p-3 text-sm text-[#374151]">{notice}</div>}

      <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
        <div className="grid gap-2 md:grid-cols-3 xl:grid-cols-6">
          <FilterSelect value={filters.range} onChange={(range) => setFilters({ ...filters, range })}>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last_7_days">Last 7 days</option>
            <option value="last_30_days">Last 30 days</option>
            <option value="custom">Custom date range</option>
          </FilterSelect>
          <FilterSelect value={filters.deviceType} onChange={(deviceType) => setFilters({ ...filters, deviceType })}>
            <option value="">All devices</option><option value="mobile">Mobile</option><option value="desktop">Desktop</option><option value="tablet">Tablet</option>
          </FilterSelect>
          <FilterSelect value={filters.source} onChange={(source) => setFilters({ ...filters, source })}>
            <option value="">All sources</option><option value="direct">Direct</option><option value="google">Google</option><option value="facebook">Facebook</option><option value="instagram">Instagram</option><option value="whatsapp">WhatsApp</option><option value="referral">Referral</option>
          </FilterSelect>
          <FilterSelect value={filters.pageType} onChange={(pageType) => setFilters({ ...filters, pageType })}>
            <option value="">All page types</option><option value="product">Product</option><option value="catalogue">Catalogue / Downloads</option><option value="contact">Contact</option><option value="blog">Blog</option><option value="solution">Solution</option>
          </FilterSelect>
          <Input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, range: "custom", start: e.target.value })} />
          <Input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, range: "custom", end: e.target.value })} />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Card label="Total visits" value={cards.totalVisits} Icon={Eye} />
        <Card label="Unique visitors" value={cards.uniqueVisitors} Icon={Users} />
        <Card label="Today visits" value={cards.todayVisits} Icon={Activity} />
        <Card label="This week visits" value={cards.thisWeekVisits} Icon={TrendingUp} />
        <Card label="This month visits" value={cards.thisMonthVisits} Icon={BarChart3} />
        <Card label="Total downloads" value={cards.totalDownloads} Icon={Download} />
        <Card label="Catalogue downloads" value={cards.totalCatalogueDownloads} Icon={FileDown} />
        <Card label="Brochure/PDF downloads" value={cards.totalBrochurePdfDownloads} Icon={FileDown} />
        <Card label="Top visited page" value={cards.topVisitedPage} Icon={Eye} />
        <Card label="Top download item" value={cards.topDownloadItem} Icon={Download} />
        <Card label="New leads generated" value={cards.newLeadsGenerated} Icon={MousePointerClick} />
        <Card label="Mobile status" value={settings.enabled ? "Tracking on" : "Tracking off"} Icon={Smartphone} />
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        <Panel title="Visits over time">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={charts.visitsOverTime || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Area type="monotone" dataKey="count" stroke="#111827" fill="#111827" fillOpacity={0.16} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Downloads over time">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={charts.downloadsOverTime || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Area type="monotone" dataKey="count" stroke="#F97316" fill="#F97316" fillOpacity={0.18} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      <div className="grid gap-5 xl:grid-cols-3">
        <Panel title="Top pages">
          <MiniBar data={pages.charts?.topPages || []} />
        </Panel>
        <Panel title="Source wise traffic">
          <MiniPie data={pages.charts?.sourceWiseTraffic || []} />
        </Panel>
        <Panel title="Device wise traffic">
          <MiniPie data={pages.charts?.deviceWiseTraffic || []} />
        </Panel>
      </div>

      <div className="grid gap-5 xl:grid-cols-3">
        <Card label="Downloads to enquiries" value={`${conversion.downloadsToEnquiries || 0}%`} Icon={Download} />
        <Card label="Visits to WhatsApp clicks" value={`${conversion.visitsToWhatsappClicks || 0}%`} Icon={MousePointerClick} />
        <Card label="Product views to leads" value={`${conversion.productViewsToLeads || 0}%`} Icon={TrendingUp} />
      </div>

      <Panel title="Tips & Improvements">
        <div className="grid gap-3 md:grid-cols-2">
          {tips.map((tip, index) => (
            <div key={`${tip.type}-${index}`} className="rounded-sm border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <div className="mb-2 flex items-center gap-2 font-extrabold text-[#111827]"><Lightbulb className="h-4 w-4" />{tip.type}</div>
              {tip.message}
            </div>
          ))}
          {!tips.length && <div className="text-sm text-[#6B7280]">No tips yet. Analytics will generate recommendations after visitor activity is recorded.</div>}
        </div>
      </Panel>

      <Panel title="Page Visit Analytics" action={<span className="text-xs text-[#6B7280]">{loading ? "Loading..." : `${pages.items?.length || 0} pages`}</span>}>
        <div className="overflow-x-auto">
          <table className="min-w-[1250px] w-full text-left text-sm">
            <thead className="bg-[#F9FAFB] text-[11px] uppercase tracking-[0.12em] text-[#6B7280]">
              <tr>
                {["Page URL", "Page title", "Total visits", "Unique visitors", "Avg time", "Bounce", "Last visited", "Device", "Source", "City/state/country"].map((head) => <th key={head} className="px-3 py-3">{head}</th>)}
              </tr>
            </thead>
            <tbody>
              {(pages.items || []).map((row) => (
                <tr key={row.pageUrl} className="border-b border-[#F3F4F6]">
                  <td className="max-w-[260px] break-words px-3 py-3">{row.pageUrl}</td>
                  <td className="max-w-[240px] break-words px-3 py-3 font-semibold">{row.pageTitle || "-"}</td>
                  <td className="px-3 py-3">{fmt(row.totalVisits)}</td>
                  <td className="px-3 py-3">{fmt(row.uniqueVisitors)}</td>
                  <td className="px-3 py-3">{Math.round(row.averageTimeOnPage || 0)}s</td>
                  <td className="px-3 py-3">{fmt(row.bounceCount)}</td>
                  <td className="px-3 py-3">{shortDate(row.lastVisitedDate)}</td>
                  <td className="px-3 py-3">{row.deviceType || "-"}</td>
                  <td className="px-3 py-3">{row.source || "-"}</td>
                  <td className="px-3 py-3">{[row.city, row.state, row.country].filter(Boolean).join(", ") || "-"}</td>
                </tr>
              ))}
              {!pages.items?.length && <tr><td colSpan={10} className="px-3 py-10 text-center text-[#6B7280]">No page visits found for the selected filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="Download Analytics">
        <div className="grid gap-5 xl:grid-cols-[1fr_320px]">
          <div className="overflow-x-auto">
            <table className="min-w-[1100px] w-full text-left text-sm">
              <thead className="bg-[#F9FAFB] text-[11px] uppercase tracking-[0.12em] text-[#6B7280]">
                <tr>
                  {["File name", "Product/category", "Page URL", "Downloads", "Unique", "IP/session", "Last date", "Device", "Source/referrer"].map((head) => <th key={head} className="px-3 py-3">{head}</th>)}
                </tr>
              </thead>
              <tbody>
                {(downloads.items || []).map((row) => (
                  <tr key={`${row.fileUrl}-${row.fileName}`} className="border-b border-[#F3F4F6]">
                    <td className="max-w-[220px] break-words px-3 py-3 font-semibold">{row.fileName || row.fileUrl || "-"}</td>
                    <td className="px-3 py-3">{[row.productName, row.category].filter(Boolean).join(" / ") || "-"}</td>
                    <td className="max-w-[240px] break-words px-3 py-3">{row.pageUrl || "-"}</td>
                    <td className="px-3 py-3">{fmt(row.downloadCount)}</td>
                    <td className="px-3 py-3">{fmt(row.uniqueDownloadCount)}</td>
                    <td className="max-w-[160px] break-words px-3 py-3">{row.lastIpAddress || "-"} / {row.lastSessionId || "-"}</td>
                    <td className="px-3 py-3">{shortDate(row.lastDownloadedAt)}</td>
                    <td className="px-3 py-3">{row.deviceType || "-"}</td>
                    <td className="max-w-[220px] break-words px-3 py-3">{row.source || "-"} {row.referrer ? `/ ${row.referrer}` : ""}</td>
                  </tr>
                ))}
                {!downloads.items?.length && <tr><td colSpan={9} className="px-3 py-10 text-center text-[#6B7280]">No downloads found for the selected filters.</td></tr>}
              </tbody>
            </table>
          </div>
          <MiniBar data={downloads.charts?.topDownloads || []} />
        </div>
      </Panel>

      <Panel title="Lead Conversion Tracking">
        <div className="grid gap-5 xl:grid-cols-[320px_1fr]">
          <MiniBar data={events.eventCounts || []} />
          <div className="overflow-x-auto">
            <table className="min-w-[900px] w-full text-left text-sm">
              <thead className="bg-[#F9FAFB] text-[11px] uppercase tracking-[0.12em] text-[#6B7280]">
                <tr>
                  {["Event", "Label", "Page URL", "Product", "Visitor/session", "Device", "Source", "Date"].map((head) => <th key={head} className="px-3 py-3">{head}</th>)}
                </tr>
              </thead>
              <tbody>
                {(events.items || []).map((row) => (
                  <tr key={row.id} className="border-b border-[#F3F4F6]">
                    <td className="px-3 py-3 font-semibold">{row.eventType}</td>
                    <td className="max-w-[180px] break-words px-3 py-3">{row.eventLabel || "-"}</td>
                    <td className="max-w-[220px] break-words px-3 py-3">{row.pageUrl || "-"}</td>
                    <td className="px-3 py-3">{row.productName || row.productId || "-"}</td>
                    <td className="max-w-[180px] break-words px-3 py-3">{row.visitorId || "-"} / {row.sessionId || "-"}</td>
                    <td className="px-3 py-3">{row.deviceType || "-"}</td>
                    <td className="px-3 py-3">{row.source || "-"}</td>
                    <td className="px-3 py-3">{shortDate(row.createdAt)}</td>
                  </tr>
                ))}
                {!events.items?.length && <tr><td colSpan={8} className="px-3 py-10 text-center text-[#6B7280]">No conversion events found for the selected filters.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </Panel>
    </div>
  );
}

function MiniBar({ data }) {
  const rows = (data || []).slice(0, 10);
  const max = Math.max(1, ...rows.map((row) => Number(row.count || 0)));
  return (
    <div className="space-y-3">
      {rows.map((row, index) => {
        const count = Number(row.count || 0);
        const width = `${Math.max(4, Math.round((count / max) * 100))}%`;
        return (
          <div key={`${row.name || "item"}-${index}`} className="grid gap-1">
            <div className="flex items-start justify-between gap-3 text-sm">
              <div className="min-w-0 break-words font-semibold leading-snug text-[#111827]" title={row.name}>
                {row.name || "-"}
              </div>
              <div className="shrink-0 font-extrabold text-[#111827]">{fmt(count)}</div>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-[#E5E7EB]">
              <div className="h-full rounded-full bg-[#111827]" style={{ width }} />
            </div>
          </div>
        );
      })}
      {!rows.length && <div className="rounded-sm border border-dashed border-[#D1D5DB] p-6 text-center text-sm text-[#6B7280]">No chart data found.</div>}
    </div>
  );
}

function MiniPie({ data }) {
  return (
    <div className="h-72">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={data || []} dataKey="count" nameKey="name" outerRadius={90} label>
            {(data || []).map((entry, index) => <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />)}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}
